import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import UserRoleForm from './UserRoleForm';
import { Role } from '../../api/roleApi';

interface EditRoleDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (role: { 
    id: string;
    name: string; 
    description: string; 
    status: 'active' | 'inactive' 
  }) => Promise<void>;
  role: Role | null;
}

const EditRoleDialog: React.FC<EditRoleDialogProps> = ({ 
  open, 
  onClose, 
  onSave,
  role: initialRole
}) => {
  const [formData, setFormData] = useState<{
    id: string;
    name: string;
    description: string;
    isActive: boolean;
    isSystem: boolean;
    userCount: number;
  }>({
    id: '',
    name: '',
    description: '',
    isActive: true,
    isSystem: false,
    userCount: 0,
  });
  
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update form data when role changes
  useEffect(() => {
    if (initialRole) {
      setFormData({
        id: initialRole.id,
        name: initialRole.name,
        description: initialRole.description || '',
        isActive: initialRole.isActive,
        isSystem: initialRole.isSystem || false,
        userCount: initialRole.userCount || 0,
      });
    }
  }, [initialRole]);

  const handleFieldChange = (
    field: 'name' | 'description' | 'isActive' | 'isSystem' | 'userCount',
    value: string | boolean | number
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      return;
    }
    
    setIsSubmitting(true);
    try {
      await onSave({
        id: formData.id,
        name: formData.name,
        description: formData.description,
        status: formData.isActive ? 'active' : 'inactive'
      });
    } catch (error) {
      console.error('Failed to update role:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit,
        sx: { 
          borderRadius: 2,
          minHeight: fullScreen ? '100%' : 'auto',
          maxHeight: '90vh',
          m: 0,
          ...(fullScreen && {
            borderRadius: 0,
          }),
        },
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        p: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
      }}>
        <Typography variant="h6" component="div">
          Edit Role
        </Typography>
        <IconButton 
          edge="end" 
          color="inherit" 
          onClick={onClose}
          aria-label="close"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              color: 'text.primary',
              backgroundColor: 'action.hover',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, backgroundColor: 'background.paper' }}>
        <Box sx={{ p: 3 }}>
          <UserRoleForm
            role={{
              name: formData.name,
              description: formData.description,
              isActive: formData.isActive,
              isSystem: formData.isSystem,
              userCount: formData.userCount
            }}
            onChange={(field, value) => {
              if (field === 'isActive') {
                handleFieldChange(field, value as boolean);
              } else if (field === 'name' || field === 'description') {
                handleFieldChange(field, value as string);
              }
            }}
            isEditMode={true}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ 
        p: 2, 
        borderTop: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
      }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          disabled={isSubmitting}
          sx={{ mr: 1 }}
        >
          Cancel
        </Button>
        <Button 
          type="submit"
          variant="contained"
          color="primary"
          disabled={isSubmitting || !formData.name.trim()}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
        >
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditRoleDialog;
